/**
 * @description
 * ErrorBoundary component for the MediConnect Healthcare Information System
 * Catches and handles JavaScript errors anywhere in the child component tree,
 * logs those errors, and displays a fallback UI for better user experience.
 * 
 * Key features:
 * - Catches JavaScript errors during rendering, in lifecycle methods, and in constructors
 * - Logs errors to the console for debugging while displaying user-friendly messages
 * - Can be configured to send error reports to monitoring services
 * - Supports custom fallback components
 * - Handles healthcare-specific error types and provides HIPAA-compliant information
 * 
 * @example
 * // Basic usage
 * <ErrorBoundary>
 *   <ChildComponent />
 * </ErrorBoundary>
 * 
 * // With custom fallback
 * <ErrorBoundary FallbackComponent={CustomErrorFallback}>
 *   <ChildComponent />
 * </ErrorBoundary>
 * 
 * @dependencies
 * - react: For component rendering
 * - ./ErrorFallback: Default fallback UI
 */

import React, { Component, ReactNode, ErrorInfo } from 'react';
import ErrorFallback from './ErrorFallback';
import { HealthcareApiError } from '../../types/error-types';

// Types for the error boundary
export interface ErrorBoundaryProps {
  children: ReactNode;
  FallbackComponent?: React.ComponentType<ErrorFallbackProps>;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  onReset?: () => void;
  resetKeys?: Array<any>;
}

export interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export interface ErrorFallbackProps {
  error: Error;
  resetErrorBoundary: () => void;
}

/**
 * ErrorBoundary component that catches JavaScript errors in child components,
 * logs them, and displays a fallback UI
 */
class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null
    };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    // Update state so the next render shows the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Log the error to an error reporting service
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    
    // Call the onError callback if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
    
    // Send HIPAA-compliant error information to monitoring service
    // Do not include PHI in error reports
    this.logErrorToService(error, errorInfo);
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps): void {
    // Reset the error boundary when any of the resetKeys change
    if (
      this.state.hasError &&
      this.props.resetKeys &&
      prevProps.resetKeys &&
      this.props.resetKeys.some((key, index) => key !== prevProps.resetKeys?.[index])
    ) {
      this.resetErrorBoundary();
    }
  }

  logErrorToService(error: Error, _errorInfo: ErrorInfo): void {
    // Sanitize the error for HIPAA compliance before sending
    // Never include PHI in error reports
    const sanitizedError = this.sanitizeErrorForReport(error);
    
    // Placeholder for sending error to monitoring service
    // actual implementation would use a service like Sentry or LogRocket
    if (import.meta.env.PROD) {
      // In production, we'd send to an actual service
      // Example: errorReportingService.captureException(sanitizedError);
      console.log('Would send to error reporting service in production', sanitizedError);
    }
  }

  sanitizeErrorForReport(error: Error): object {
    // Create a sanitized version of the error for reporting
    // This ensures no PHI is included in error reports
    
    // Add specific handling for undefined variable errors
    if (error.message && error.message.includes('undefined')) {
      // Handle "x is undefined" errors more gracefully
      const match = error.message.match(/([a-zA-Z0-9_]+) is undefined/);
      const variableName = match ? match[1] : 'unknown';
      
      return {
        name: error.name,
        message: `Missing required data: ${variableName}`,
        type: 'ReferenceError',
        safeMessage: `A required variable was not available. Technical details: ${variableName} was undefined.`
      };
    }
    
    // If it's our custom HealthcareApiError, use its sanitization methods
    if (error instanceof HealthcareApiError) {
      return {
        name: error.name,
        message: error.getUserFriendlyMessage(),
        code: error.code,
        statusCode: error.statusCode,
        // Do not include the path or specific identifiers
      };
    }
    
    // For standard errors, just include non-PHI information
    return {
      name: error.name,
      message: error.message,
      stack: error.stack ? this.sanitizeStackTrace(error.stack) : undefined
    };
  }

  sanitizeStackTrace(stack: string): string {
    // Remove potential PII/PHI from stack traces
    // This is a simple implementation - in reality, you'd want a more sophisticated approach
    return stack
      .split('\n')
      .filter(line => !line.includes('patient') && !line.includes('PHI'))
      .join('\n');
  }

  resetErrorBoundary = (): void => {
    this.setState({ hasError: false, error: null });
    if (this.props.onReset) {
      this.props.onReset();
    }
  };

  render(): ReactNode {
    const { hasError, error } = this.state;
    const { children, FallbackComponent = ErrorFallback } = this.props;

    if (hasError && error) {
      return (
        <FallbackComponent
          error={error}
          resetErrorBoundary={this.resetErrorBoundary}
        />
      );
    }

    return children;
  }
}

export default ErrorBoundary; 